import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ActiveModalsContextType = {
  isAnyModalOpen: boolean;
  registerModal: () => () => void;
};

const ActiveModalsContext = createContext<ActiveModalsContextType | null>(null);

export const useActiveModalsContext = () => {
  const context = useContext(ActiveModalsContext);
  if (!context) {
    throw new Error(
      'useActiveModalsContext used outside of ActiveModalsProvider. Ensure you have an <ActiveModalsProvider> higher in the tree.',
    );
  }
  return context;
};

export const ActiveModalsProvider = ({ children }: React.PropsWithChildren) => {
  const [activeModals, setActiveModals] = useState(0);

  const registerModal = useCallback(() => {
    setActiveModals((prev) => prev + 1);
    return () => setActiveModals((prev) => prev - 1);
  }, []);

  const value = useMemo(
    () => ({
      isAnyModalOpen: activeModals > 0,
      registerModal,
    }),
    [activeModals, registerModal],
  );

  return <ActiveModalsContext.Provider value={value}>{children}</ActiveModalsContext.Provider>;
};
